/**
 * Admin-created custom pages: /pages/<slug>.
 * Content comes from the page builder; nothing here is hard-coded.
 */
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { BlockCanvas } from "@/components/cms/BlockCanvas";
import { useCmsEdit } from "@/components/cms/cms-edit";
import { adminGetCmsPage, getPublishedCmsPage } from "@/lib/cms.functions";
import type { Block } from "@/lib/cms/blocks";

export const Route = createFileRoute("/pages/$slug")({
  loader: async ({ params }) => {
    const page = await getPublishedCmsPage({ data: { path: `/pages/${params.slug}` } });
    return { page };
  },
  head: ({ loaderData, params }) => {
    const page = loaderData?.page;
    const url = `https://truwe.in/pages/${params.slug}`;
    const title = page?.seo.title || page?.title || "TruWe";
    const description =
      page?.seo.description || "Research-backed wellness from TruWe, made in India.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        ...(page?.seo.image
          ? [
              { property: "og:image", content: page.seo.image },
              { name: "twitter:image", content: page.seo.image },
            ]
          : []),
        ...(page?.seo.noindex ? [{ name: "robots", content: "noindex, nofollow" }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: CmsPage,
  errorComponent: () => (
    <div className="mx-auto max-w-xl px-4 py-24 text-center">
      <h1 className="font-display text-xl font-semibold">This page didn't load</h1>
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-xl px-4 py-24 text-center">
      <h1 className="font-display text-xl font-semibold">Page not found</h1>
    </div>
  ),
});

function CmsPage() {
  const { slug } = Route.useParams();
  const { page } = Route.useLoaderData();
  const path = `/pages/${slug}`;
  const cms = useCmsEdit();
  const getDraft = useServerFn(adminGetCmsPage);

  const draft = useQuery({
    queryKey: ["cms", "page", "draft", path],
    queryFn: () => getDraft({ data: { path } }),
    enabled: cms.active,
    retry: false,
  });

  if (cms.active) {
    if (draft.isLoading) return null;
    if (!draft.data) throw notFound();
    const blocks: Block[] = draft.data.draft.blocks;
    return <BlockCanvas path={path} label={draft.data.title || path} blocks={blocks} />;
  }

  if (!page) throw notFound();
  return <BlockCanvas path={path} label={page.title} blocks={page.doc.blocks} />;
}
